'server-only'

import { JSX, PropsWithChildren } from 'react'
import { Box, Button } from '@chakra-ui/react'
import Link from 'next/link'

type Props = PropsWithChildren & {
  href?: string
  type?: 'button' | 'reset' | 'submit' | undefined
  isDisabled?: boolean
  onClick?: () => void
  as?: string
  height?: string
  className?: string
  id?: string
  openNewTab?: boolean
}

export const ButtonServer = ({
  children,
  href,
  type,
  isDisabled = false,
  onClick,
  height,
  className,
  openNewTab,
}: Props): JSX.Element => {
  if (href) {
    return (
      <Link href={href} passHref={undefined} target={openNewTab ? '_blank' : undefined}>
        <Box
          className={className}
          background={isDisabled ? 'green.a250' : 'green.a200'}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: height || '54px',
            fontSize: 'var(--chakra-font-sizes-sm)',
            transition: 'background 0.1s ease-in, color 0.1s ease-in',
            borderRadius: 'var(--chakra-border-radius-small)',
            textAlign: 'center',
            fontWeight: 600,
            cursor: 'pointer',
            color: 'white',
            pointerEvents: isDisabled ? 'none' : 'all',
          }}
          _hover={{
            backgroundColor: 'green-a250',
          }}
          _focus={{
            outline: 'none',
            boxShadow: 'none',
          }}
        >
          {children}
        </Box>
      </Link>
    )
  }

  return (
    <Button
      className={className}
      type={type}
      fontSize="sm"
      borderRadius="small"
      background={isDisabled ? 'green.a250' : 'green.a200'}
      wordWrap="break-word"
      maxWidth="100%"
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '54px',
        transition: 'background 0.1s ease-in, color 0.1s ease-in',
        textAlign: 'center',
        fontWeight: 600,
        cursor: 'pointer',
        color: 'white',
        pointerEvents: isDisabled ? 'none' : 'all',
      }}
      _hover={{
        backgroundColor: 'green-a250',
      }}
      _focus={{
        outline: 'none',
        boxShadow: 'none',
      }}
      onClick={onClick}
    >
      {children}
    </Button>
  )
}

export const ButtonTextServer = ({
  href,
  children,
  onClick,
  type,
  isLight = false,
  isSmall = false,
  isDimmed = false,
  as,
  className,
}: Props & { isLight?: boolean; isSmall?: boolean; isDimmed?: boolean }): JSX.Element => {
  if (href) {
    return (
      <Link href={href} as={as} passHref>
        <Box
          className={className}
          fontSize={isSmall ? 'sm' : 'md'}
          color={isLight ? 'white' : 'green.a200'}
          style={{
            cursor: 'pointer',
            opacity: isDimmed ? 0.5 : 1,
          }}
        >
          {children}
        </Box>
      </Link>
    )
  }

  return (
    <Button
      className={className}
      type={type}
      fontSize={isSmall ? 'sm' : 'md'}
      color={isLight ? 'white' : 'green.a200'}
      style={{
        fontWeight: isSmall ? 300 : 600,
        cursor: 'pointer',
        opacity: isDimmed ? 0.5 : 1,
      }}
      onClick={onClick}
    >
      {children}
    </Button>
  )
}

export const RentButtonServer = ({
  children,
  isRentingInProgress,
  onClick,
}: PropsWithChildren & { onClick?: () => void; isRentingInProgress: boolean }): JSX.Element => {
  return (
    <Button
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '54px',
        fontSize: 'var(--chakra-font-sizes-sm)',
        transition: 'background 0.1s ease-in, color 0.1s ease-in',
        borderRadius: 'var(--chakra-border-radius-small)',
        textAlign: 'center',
        fontWeight: 600,
        cursor: 'pointer',

        background: isRentingInProgress ? 'var(--chakra-colors-green-a125)' : 'var(--chakra-colors-green-a200)',
        color: isRentingInProgress ? 'var(--chakra-colors-green-a200)' : 'var(--chakra-colors-white)',
      }}
      _hover={{
        backgroundColor: 'green-a250',
      }}
      _focus={{
        outline: 'none',
        boxShadow: 'none',
      }}
      onClick={onClick}
    >
      {children}
    </Button>
  )
}

export const ButtonWhiteServer = ({
  children,
  isDisabled = false,
  onClick,
  type,
  href,
  height,
  className,
  id,
}: Props) => {
  if (href) {
    return (
      <Link href={href} passHref={undefined}>
        <Box
          className={`${className}`}
          color="updatedLeid.lightBrick"
          background="updatedLeid.brick"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: height || '54px',
            fontSize: 'var(--chakra-font-sizes-sm)',
            transition: 'background 0.1s ease-in, color 0.1s ease-in',
            borderRadius: 'var(--chakra-border-radius-small)',
            textAlign: 'center',
            fontWeight: 600,
            cursor: 'pointer',

            pointerEvents: isDisabled ? 'none' : 'all',
            opacity: isDisabled ? 0.5 : 1,
          }}
          _hover={{
            backgroundColor: 'gray.a100',
          }}
          _focus={{
            outline: 'none',
            boxShadow: 'none',
          }}
        >
          {children}
        </Box>
      </Link>
    )
  }

  return (
    <Button
      className={`${className}`}
      type={type}
      color="green.a200"
      background="white"
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '54px',
        fontSize: 'var(--chakra-font-sizes-sm)',
        transition: 'background 0.1s ease-in, color 0.1s ease-in',
        borderRadius: 'var(--chakra-border-radius-small)',
        textAlign: 'center',
        fontWeight: 600,
        cursor: 'pointer',

        pointerEvents: isDisabled ? 'none' : 'all',
      }}
      onClick={onClick}
      _hover={{
        backgroundColor: 'gray.a100',
      }}
      _focus={{
        outline: 'none',
        boxShadow: 'none',
      }}
    >
      {children}
    </Button>
  )
}

export const ActionButtonServer = ({
  children,
  href,
  type,
  isDisabled = false,
  onClick,
  height,
  className,
}: Props): JSX.Element => {
  if (href) {
    return (
      <Link href={href} passHref={undefined}>
        <Box
          className={className}
          fontSize="sm"
          borderRadius="small"
          background={isDisabled ? 'leid.lysGul' : 'leid.gul'}
          color="black"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: height || '54px',
            transition: 'background 0.1s ease-in, color 0.1s ease-in',
            textAlign: 'center',
            fontWeight: 600,
            cursor: 'pointer',
            pointerEvents: isDisabled ? 'none' : 'all',
          }}
          _hover={{
            backgroundColor: isDisabled ? 'leid.lysGulLight' : 'leid.gulLight',
          }}
          _focus={{
            outline: 'none',
            boxShadow: 'none',
          }}
        >
          {children}
        </Box>
      </Link>
    )
  }

  return (
    <Button
      className={className}
      type={type}
      fontSize="sm"
      borderRadius="small"
      background={isDisabled ? 'leid.lysGul' : 'leid.gul'}
      color="black"
      wordWrap="break-word"
      maxWidth="100%"
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '54px',
        transition: 'background 0.1s ease-in, color 0.1s ease-in',
        textAlign: 'center',
        fontWeight: 600,
        cursor: 'pointer',
        pointerEvents: isDisabled ? 'none' : 'all',
      }}
      _hover={{
        backgroundColor: isDisabled ? 'leid.lysGulLight' : 'leid.gulLight',
      }}
      _focus={{
        outline: 'none',
        boxShadow: 'none',
      }}
      onClick={onClick}
    >
      {children}
    </Button>
  )
}
